import React, { useMemo, useState } from "react";
import {
    ATHLETE_ONBOARDING_WIZARD_STEPS,
    type AthleteOnboardingWizardStep,
} from "./athleteOnboardingWizardSteps";
import polarisLogo from "../images/Polaris Logo Dark.svg";
import "../styles/athlete-onboarding-wizard.css";

type AthleteOnboardingWizardProps = {
    open: boolean;
    onDismiss: () => void | Promise<void>;
};

function primaryButtonLabel(step: AthleteOnboardingWizardStep, isLastStep: boolean): string {
    if (step.kind === "welcome") {
        return "Get started";
    }
    if (isLastStep) {
        return "Start my journey";
    }
    return "Next";
}

function primaryButtonTestId(step: AthleteOnboardingWizardStep, isLastStep: boolean): string {
    if (step.kind === "welcome") {
        return "athlete-onboarding-wizard-get-started";
    }
    if (isLastStep) {
        return "athlete-onboarding-wizard-start-journey";
    }
    return "athlete-onboarding-wizard-next";
}

function renderWelcomeParagraph(paragraph: string, highlightedPhrase?: string) {
    if (!highlightedPhrase) {
        return paragraph;
    }
    const index = paragraph.indexOf(highlightedPhrase);
    if (index === -1) {
        return paragraph;
    }
    return (
        <>
            {paragraph.slice(0, index)}
            <strong>{highlightedPhrase}</strong>
            {paragraph.slice(index + highlightedPhrase.length)}
        </>
    );
}

export default function AthleteOnboardingWizard({ open, onDismiss }: AthleteOnboardingWizardProps) {
    const [stepIndex, setStepIndex] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const steps = useMemo(() => ATHLETE_ONBOARDING_WIZARD_STEPS, []);
    const currentStep = steps[stepIndex];
    const isLastStep = stepIndex === steps.length - 1;

    if (!open || !currentStep) {
        return null;
    }

    const handleDismiss = async () => {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        try {
            await onDismiss();
            setStepIndex(0);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePrimary = async () => {
        if (isLastStep) {
            await handleDismiss();
            return;
        }
        setStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
    };

    const primaryLabel = primaryButtonLabel(currentStep, isLastStep);
    const primaryTestId = primaryButtonTestId(currentStep, isLastStep);

    return (
        <div className="athlete-onboarding-wizard-overlay" data-testid="athlete-onboarding-wizard">
            <div
                className={
                    currentStep.kind === "welcome"
                        ? "athlete-onboarding-wizard athlete-onboarding-wizard--welcome"
                        : "athlete-onboarding-wizard"
                }
                role="dialog"
                aria-modal="true"
                aria-labelledby="athlete-onboarding-wizard-title"
                data-testid={`athlete-onboarding-wizard-step-${currentStep.id}`}
            >
                <div className="athlete-onboarding-wizard__body">
                    {currentStep.kind === "welcome" && (
                        <>
                            <img
                                src={polarisLogo}
                                alt="Polaris Athlete"
                                className="athlete-onboarding-wizard__logo"
                            />
                            <h2 id="athlete-onboarding-wizard-title" className="athlete-onboarding-wizard__title">
                                {currentStep.title}
                            </h2>
                            <p className="athlete-onboarding-wizard__subtitle">{currentStep.subtitle}</p>
                            {currentStep.body.map((paragraph) => (
                                <p key={paragraph} className="athlete-onboarding-wizard__text">
                                    {renderWelcomeParagraph(paragraph, currentStep.highlightedPhrase)}
                                </p>
                            ))}
                        </>
                    )}

                    {currentStep.kind === "preview-grid" && (
                        <>
                            <h2 id="athlete-onboarding-wizard-title" className="athlete-onboarding-wizard__heading">
                                {currentStep.heading}
                            </h2>
                            <p className="athlete-onboarding-wizard__subtitle">{currentStep.subtitle}</p>
                            <div className="athlete-onboarding-wizard__grid">
                                {currentStep.previews.map((preview) => (
                                    <div key={preview.id} className="athlete-onboarding-wizard__preview">
                                        <div className="athlete-onboarding-wizard__preview-image-wrap">
                                            <img
                                                src={preview.image}
                                                alt={preview.imageAlt}
                                                className="athlete-onboarding-wizard__preview-image"
                                            />
                                        </div>
                                        <p className="athlete-onboarding-wizard__preview-label">{preview.label}</p>
                                        <span className="athlete-onboarding-wizard__preview-dot" aria-hidden="true" />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {currentStep.kind === "finish" && (
                        <>
                            <h2 id="athlete-onboarding-wizard-title" className="athlete-onboarding-wizard__heading">
                                {currentStep.heading}
                            </h2>
                            <p className="athlete-onboarding-wizard__text">{currentStep.body}</p>
                            <p className="athlete-onboarding-wizard__highlight">{currentStep.highlight}</p>
                        </>
                    )}
                </div>

                <div className="athlete-onboarding-wizard__footer">
                    {!isLastStep && (
                        <button
                            type="button"
                            className="athlete-onboarding-wizard__skip"
                            onClick={() => void handleDismiss()}
                            disabled={isSubmitting}
                            data-testid="athlete-onboarding-wizard-skip"
                        >
                            Skip
                        </button>
                    )}
                    <button
                        type="button"
                        className="athlete-onboarding-wizard__primary"
                        onClick={() => void handlePrimary()}
                        disabled={isSubmitting}
                        data-testid={primaryTestId}
                    >
                        {primaryLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
